import type { XbotBinding, XbotBindingTarget, XbotErrorBody, XbotSuggestion, XbotUtf16Range } from "@dto/pyxbot-agent.dto";
import { XBOT_MAX_BINDINGS } from "@dto/pyxbot-agent.dto";

// Draft = text + object picks. Ranges are UTF-16 code units (what the service checks against); a
// pick moves with its words and vanishes if they change.

/** What the picked line under the editor says about the object. */
export interface DraftShown {
  label: string;
  distinguish: string[];
  /** Kept for later; the picked line does not draw it as a link. */
  ui_path: string | null;
}

export interface DraftBinding {
  range: XbotUtf16Range;
  /** Exactly the words at range. */
  text: string;
  target: XbotBindingTarget;
  shown: DraftShown;
  /** The service refused this pick; it is shown struck through and is not sent until picked again. */
  struck?: boolean;
}

export interface Draft {
  text: string;
  /** In the order of the text, never overlapping. */
  bindings: DraftBinding[];
}

export const EMPTY_DRAFT: Draft = { text: "", bindings: [] };

/** The longest message the chat sends; the server refuses a longer one. */
export const DRAFT_MESSAGE_MAX = 2000;

/** The longest fragment the service searches for. */
export const FRAGMENT_MAX = 200;

export const DRAFT_PROBLEMS = {
  tooLong: `The message is longer than ${DRAFT_MESSAGE_MAX} characters. Shorten it to send it.`,
  malformed: "The message holds a character the chat cannot send. Remove it and try again.",
  tooMany: `At most ${XBOT_MAX_BINDINGS} objects can be picked in one message. Remove some of them to send it.`,
  lost: (name: string) => `The selection of "${name}" no longer matches the text. Pick it again, or remove it with × to send the sentence without it.`,
};

function isHighSurrogate(unit: number): boolean {
  return unit >= 0xd800 && unit <= 0xdbff;
}

function isLowSurrogate(unit: number): boolean {
  return unit >= 0xdc00 && unit <= 0xdfff;
}

// Blanks String.prototype.trim removes, listed by code, so a fragment boundary matches trim exactly.
const BLANK_UNITS: ReadonlySet<number> = new Set([
  0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x20, 0xa0, 0x1680,
  0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a,
  0x2028, 0x2029, 0x202f, 0x205f, 0x3000, 0xfeff,
]);

// Scripts with no upper/lower case, by block; cased letters (Latin, Greek, Cyrillic...) are told apart by case instead.
const UNCASED_LETTER_BLOCKS: readonly (readonly [number, number])[] = [
  [0x0590, 0x05ff], // Hebrew
  [0x0600, 0x06ff], // Arabic
  [0x0900, 0x097f], // Devanagari
  [0x0e00, 0x0e7f], // Thai
  [0x3040, 0x30ff], // Hiragana, Katakana
  [0x3400, 0x4dbf], // CJK extension A
  [0x4e00, 0x9fff], // CJK
  [0xac00, 0xd7af], // Hangul
];

function isLetterOrDigit(char: string): boolean {
  if (!char) {
    return false;
  }
  const code = char.codePointAt(0);
  if ((code >= 0x30 && code <= 0x39) || (code >= 0x41 && code <= 0x5a) || (code >= 0x61 && code <= 0x7a)) {
    return true;
  }
  if (code < 0x80) {
    return false;
  }
  if (char.toLowerCase() !== char.toUpperCase()) {
    return true;
  }
  return UNCASED_LETTER_BLOCKS.some(([from, to]) => code >= from && code <= to);
}

/** The whole character (one or two units) that ends at `end`, or "" at the start. */
function charBefore(text: string, end: number): string {
  if (end <= 0) {
    return "";
  }
  const last = text.charCodeAt(end - 1);
  if (end >= 2 && isLowSurrogate(last) && isHighSurrogate(text.charCodeAt(end - 2))) {
    return text.slice(end - 2, end);
  }
  return text.slice(end - 1, end);
}

/** The whole character (one or two units) that starts at `start`, or "" at the end. */
function charAt(text: string, start: number): string {
  if (start >= text.length) {
    return "";
  }
  const first = text.charCodeAt(start);
  if (isHighSurrogate(first) && start + 1 < text.length && isLowSurrogate(text.charCodeAt(start + 1))) {
    return text.slice(start, start + 2);
  }
  return text.slice(start, start + 1);
}

/** A letter, a digit, "-" or "_" beside a picked name makes it part of another name. */
function isNameChar(char: string): boolean {
  return char === "-" || char === "_" || isLetterOrDigit(char);
}

// A trailing dot only breaks off a name when a letter/digit follows it too ("app-prod.x" isn't just "app-prod").
function standsAlone(text: string, range: XbotUtf16Range): boolean {
  const before = charBefore(text, range.start);
  if (isNameChar(before)) {
    return false;
  }
  if (before === "." && isLetterOrDigit(charBefore(text, range.start - 1))) {
    return false;
  }
  const after = charAt(text, range.end);
  if (isNameChar(after)) {
    return false;
  }
  if (after === "." && isLetterOrDigit(charAt(text, range.end + 1))) {
    return false;
  }
  return true;
}

/** Whether the text is well formed UTF-16: no lone half of a surrogate pair, which the service cannot store. */
export function isWellFormedUtf16(text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i);
    if (isHighSurrogate(unit)) {
      if (i + 1 >= text.length || !isLowSurrogate(text.charCodeAt(i + 1))) {
        return false;
      }
      i++;
    } else if (isLowSurrogate(unit)) {
      return false;
    }
  }
  return true;
}

export interface FragmentAt {
  start: number;
  end: number;
  fragment: string;
}

// Selection if there is one, else the run of non-blank chars around the cursor; stops only at a
// blank, so a surrogate pair is never split.
export function fragmentAt(text: string, selectionStart: number, selectionEnd: number): FragmentAt | null {
  const length = text.length;
  const from = Math.max(0, Math.min(length, Math.min(selectionStart, selectionEnd)));
  const to = Math.max(0, Math.min(length, Math.max(selectionStart, selectionEnd)));
  let start = from;
  let end = to;
  if (from === to) {
    while (start > 0 && !BLANK_UNITS.has(text.charCodeAt(start - 1))) {
      start--;
    }
    while (end < length && !BLANK_UNITS.has(text.charCodeAt(end))) {
      end++;
    }
  }
  const fragment = text.slice(start, end);
  if (!fragment || fragment.length > FRAGMENT_MAX || !fragment.trim() || !isWellFormedUtf16(fragment)) {
    return null;
  }
  return { start, end, fragment };
}

/** The pick the words at range run into, if any: an offer is not asked for a name already picked. */
export function bindingOver(draft: Draft, range: XbotUtf16Range): DraftBinding | null {
  return draft.bindings.find((binding) => !binding.struck && binding.range.start < range.end && binding.range.end > range.start) ?? null;
}

/** The pick standing nearest before range: its platform and kind order the offer (siblings first). */
export function bindingBefore(draft: Draft, range: XbotUtf16Range): DraftBinding | null {
  let found: DraftBinding | null = null;
  for (const binding of draft.bindings) {
    if (!binding.struck && binding.range.end <= range.start) {
      found = binding;
    }
  }
  return found;
}

function inOrder(bindings: DraftBinding[]): DraftBinding[] {
  return bindings.slice().sort((a, b) => a.range.start - b.range.start);
}

/** Only the picks whose words are still theirs and still stand alone. */
function stillStanding(text: string, bindings: DraftBinding[]): DraftBinding[] {
  return bindings.filter((binding) => {
    const { start, end } = binding.range;
    return start >= 0 && end <= text.length && start < end
      && text.slice(start, end) === binding.text
      && standsAlone(text, binding.range);
  });
}

// Shifts picks after an edit: one wholly before stays, one wholly after moves by the delta, one the change touches is dropped.
function shifted(bindings: DraftBinding[], a: number, b: number, n: number): DraftBinding[] {
  const delta = n - (b - a);
  const kept: DraftBinding[] = [];
  for (const binding of bindings) {
    const { start, end } = binding.range;
    if (end <= a) {
      kept.push(binding);
    } else if (start >= b) {
      kept.push(delta ? { ...binding, range: { start: start + delta, end: end + delta } } : binding);
    }
  }
  return kept;
}

// Finds the changed span via longest common prefix/suffix; when the caret is known, the suffix
// match stops there so pasting text in front of a pick doesn't merge into it.
export function applyEdit(draft: Draft, next: string, caret?: number): Draft {
  const old = draft.text;
  if (old === next) {
    return draft;
  }
  const common = Math.min(old.length, next.length);
  let prefix = 0;
  let suffix = 0;
  if (typeof caret === "number" && caret >= 0 && caret <= next.length) {
    const suffixLimit = Math.min(common, next.length - caret);
    while (suffix < suffixLimit && old.charCodeAt(old.length - 1 - suffix) === next.charCodeAt(next.length - 1 - suffix)) {
      suffix++;
    }
    const prefixLimit = common - suffix;
    while (prefix < prefixLimit && old.charCodeAt(prefix) === next.charCodeAt(prefix)) {
      prefix++;
    }
  } else {
    while (prefix < common && old.charCodeAt(prefix) === next.charCodeAt(prefix)) {
      prefix++;
    }
    const suffixLimit = common - prefix;
    while (suffix < suffixLimit && old.charCodeAt(old.length - 1 - suffix) === next.charCodeAt(next.length - 1 - suffix)) {
      suffix++;
    }
  }
  // The changed part never starts or ends inside a surrogate pair.
  if (prefix > 0 && isHighSurrogate(old.charCodeAt(prefix - 1))) {
    prefix--;
  }
  if (suffix > 0 && isLowSurrogate(old.charCodeAt(old.length - suffix))) {
    suffix--;
  }
  const a = prefix;
  const b = old.length - suffix;
  const n = next.length - suffix - prefix;
  return { text: next, bindings: stillStanding(next, shifted(draft.bindings, a, b, n)) };
}

/** The pick of an offered object whose name starts at `start`. */
function pickOf(suggestion: XbotSuggestion, start: number): DraftBinding {
  return {
    range: { start, end: start + suggestion.insert_text.length },
    text: suggestion.insert_text,
    target: { item_id: suggestion.target.item_id, platform: suggestion.target.platform, kind: suggestion.target.kind },
    shown: { label: suggestion.label, distinguish: suggestion.distinguish.slice(), ui_path: suggestion.ui_path },
  };
}

// Replaces the asked-for words with the object's name and binds it; null if the words moved since,
// or the pick wouldn't stand alone — the text is never changed for a pick that can't hold.
export function acceptSuggestion(draft: Draft, range: XbotUtf16Range, fragment: string, suggestion: XbotSuggestion): Draft | null {
  const { start, end } = range;
  const insert = suggestion.insert_text;
  if (start < 0 || end > draft.text.length || start >= end || draft.text.slice(start, end) !== fragment || !insert) {
    return null;
  }
  const text = draft.text.slice(0, start) + insert + draft.text.slice(end);
  const picked = pickOf(suggestion, start);
  if (!standsAlone(text, picked.range)) {
    return null;
  }
  const others = shifted(draft.bindings, start, end, insert.length);
  return { text, bindings: stillStanding(text, inOrder([...others, picked])) };
}

const SPACE = /\s/;

/** A picked object put where the cursor stands, and where the cursor goes after it. */
export interface InsertedPick {
  draft: Draft;
  /** Right after the inserted name. */
  caret: number;
}

/**
 * The object found by name (the Objects search) put at the cursor, with a space before or after it where a word
 * would otherwise touch it, so the name stands alone and the pick holds. A cursor inside an earlier pick moves to
 * its end; the picks after the cursor shift. Null when the name could not stand there (or the text would grow past
 * what a message may hold).
 */
export function insertSuggestionAt(draft: Draft, caret: number, suggestion: XbotSuggestion): InsertedPick | null {
  const name = suggestion.insert_text;
  if (!name) {
    return null;
  }
  let at = Math.max(0, Math.min(draft.text.length, caret));
  const inside = draft.bindings.find((binding) => binding.range.start < at && at < binding.range.end);
  if (inside) {
    at = inside.range.end;
  }
  const before = at > 0 && !SPACE.test(draft.text[at - 1]) ? " " : "";
  const after = at < draft.text.length && !SPACE.test(draft.text[at]) ? " " : "";
  const insert = `${before}${name}${after}`;
  const text = draft.text.slice(0, at) + insert + draft.text.slice(at);
  if (text.length > DRAFT_MESSAGE_MAX) {
    return null;
  }
  const start = at + before.length;
  const picked = pickOf(suggestion, start);
  if (!standsAlone(text, picked.range)) {
    return null;
  }
  const others = shifted(draft.bindings, at, at, insert.length);
  return { draft: { text, bindings: stillStanding(text, inOrder([...others, picked])) }, caret: start + name.length };
}

/** The draft without the pick at index; its words stay. */
export function removeBinding(draft: Draft, index: number): Draft {
  if (index < 0 || index >= draft.bindings.length) {
    return draft;
  }
  return { text: draft.text, bindings: draft.bindings.filter((_, at) => at !== index) };
}

export interface FinalDraft {
  /** Byte for byte what the service stores. */
  message: string;
  bindings: XbotBinding[];
}

export type Finalized = FinalDraft | { problem: string };

/** What was sent, kept until the answer comes, so a refusal before the turn can put it back. */
export interface SentDraft {
  message: string;
  /** The picks as sent - ranges in the message - with what the picked line said about each. */
  bindings: DraftBinding[];
}

/** The message and its live picks measured against it, or why it cannot go. */
function finalized(draft: Draft): SentDraft | { problem: string } {
  const text = draft.text;
  const lead = text.length - text.trimStart().length;
  const message = text.trim();
  if (message.length > DRAFT_MESSAGE_MAX) {
    return { problem: DRAFT_PROBLEMS.tooLong };
  }
  if (!isWellFormedUtf16(message)) {
    return { problem: DRAFT_PROBLEMS.malformed };
  }
  const live = inOrder(draft.bindings.filter((binding) => !binding.struck));
  if (live.length > XBOT_MAX_BINDINGS) {
    return { problem: DRAFT_PROBLEMS.tooMany };
  }
  const bindings: DraftBinding[] = [];
  let lastEnd = 0;
  for (const binding of live) {
    const start = binding.range.start - lead;
    const end = binding.range.end - lead;
    if (start < lastEnd || end > message.length || start >= end || message.slice(start, end) !== binding.text) {
      return { problem: DRAFT_PROBLEMS.lost(binding.text) };
    }
    lastEnd = end;
    bindings.push({ ...binding, range: { start, end } });
  }
  return { message, bindings };
}

// Trims the message here, once — nothing after this changes a character, so picks stay measured
// against what's actually sent.
export function finalizeDraft(draft: Draft): Finalized {
  const done = finalized(draft);
  if ("problem" in done) {
    return done;
  }
  return {
    message: done.message,
    bindings: done.bindings.map((binding): XbotBinding => ({
      type: "object",
      range: { start: binding.range.start, end: binding.range.end },
      text: binding.text,
      target: { item_id: binding.target.item_id, platform: binding.target.platform, kind: binding.target.kind },
    })),
  };
}

/** What a message that can go will be kept as while it is out; null when it cannot go. */
export function sentDraftOf(draft: Draft): SentDraft | null {
  const done = finalized(draft);
  return "problem" in done ? null : done;
}

function whole(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

/** The places of the picks a refusal names: one for invalid_binding, the list for binding_not_visible. */
function refusedPlaces(error: XbotErrorBody): Set<number> {
  const places = new Set<number>();
  const detail = error.detail;
  if (typeof detail !== "object" || detail === null || Array.isArray(detail)) {
    return places;
  }
  const said = detail as { index?: unknown; bindings?: unknown };
  if (error.code === "invalid_binding") {
    const index = whole(said.index);
    if (index !== null) {
      places.add(index);
    }
  } else if (error.code === "binding_not_visible" && Array.isArray(said.bindings)) {
    for (const one of said.bindings) {
      const index = whole((one as { index?: unknown } | null)?.index);
      if (index !== null) {
        places.add(index);
      }
    }
  }
  return places;
}

// Puts a refused message back with its named picks struck through, to re-pick or drop; nothing is picked anew.
export function restoredDraft(sent: SentDraft, error: XbotErrorBody): Draft {
  const places = refusedPlaces(error);
  return {
    text: sent.message,
    bindings: sent.bindings.map((binding, index) => (places.has(index) ? { ...binding, struck: true } : binding)),
  };
}

/** How long typing may pause and still be undone as one step. */
export const TYPING_STEP_MS = 1000;

/** Undo steps kept. */
const HISTORY_MAX = 100;

export interface DraftHistory {
  past: Draft[];
  present: Draft;
  future: Draft[];
  /** When the present was pushed and whether it was a small typed change - to gather quick typing into one step. */
  lastAt: number | null;
  lastSmall: boolean;
}

export function startHistory(draft: Draft): DraftHistory {
  return { past: [], present: draft, future: [], lastAt: null, lastSmall: false };
}

// The editor's own undo only knows text, not picks, so this tracks both; quick typing gathers into
// one step, everything else is its own step.
export function pushHistory(history: DraftHistory, next: Draft, how: { now: number; small: boolean }): DraftHistory {
  if (next === history.present) {
    return history;
  }
  const gather = how.small && history.lastSmall && history.lastAt !== null
    && how.now - history.lastAt <= TYPING_STEP_MS
    && next.bindings.length === history.present.bindings.length
    && history.past.length > 0;
  if (gather) {
    return { ...history, present: next, future: [], lastAt: how.now };
  }
  const past = [...history.past, history.present].slice(-HISTORY_MAX);
  return { past, present: next, future: [], lastAt: how.now, lastSmall: how.small };
}

export function undo(history: DraftHistory): DraftHistory {
  if (!history.past.length) {
    return history;
  }
  const previous = history.past[history.past.length - 1];
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
    lastAt: null,
    lastSmall: false,
  };
}

export function redo(history: DraftHistory): DraftHistory {
  if (!history.future.length) {
    return history;
  }
  const [next, ...rest] = history.future;
  return { past: [...history.past, history.present], present: next, future: rest, lastAt: null, lastSmall: false };
}

// Puts the caret right after the changed part (matching the browser's own undo), since setting the
// text whole would otherwise jump it to the end; null when only picks changed.
export function caretAfterJump(from: string, to: string): number | null {
  if (from === to) {
    return null;
  }
  const common = Math.min(from.length, to.length);
  let prefix = 0;
  while (prefix < common && from.charCodeAt(prefix) === to.charCodeAt(prefix)) {
    prefix++;
  }
  if (prefix > 0 && isHighSurrogate(to.charCodeAt(prefix - 1))) {
    prefix--;
  }
  let suffix = 0;
  const suffixLimit = common - prefix;
  while (suffix < suffixLimit && from.charCodeAt(from.length - 1 - suffix) === to.charCodeAt(to.length - 1 - suffix)) {
    suffix++;
  }
  if (suffix > 0 && isLowSurrogate(to.charCodeAt(to.length - suffix))) {
    suffix--;
  }
  return to.length - suffix;
}

/** Whether an edit is a small typed one: one character in or out, the picks as they were. */
export function isSmallEdit(before: Draft, after: Draft): boolean {
  const grew = Math.abs(after.text.length - before.text.length);
  return grew <= 2 && after.bindings.length === before.bindings.length;
}

// Null for anything that isn't a plain text box (e.g. a slot-based editor), rather than guessing where the cursor is.
export function caretOf(element: unknown): { start: number; end: number } | null {
  if (typeof element !== "object" || element === null) {
    return null;
  }
  const box = element as { selectionStart?: unknown; selectionEnd?: unknown };
  if (typeof box.selectionStart !== "number" || typeof box.selectionEnd !== "number") {
    return null;
  }
  return { start: box.selectionStart, end: box.selectionEnd };
}
